import { CognitoIdentityServiceProvider, CognitoIdentityCredentials, config } from 'aws-sdk';
import CryptoJS from 'crypto-js';
import openNotificationWithIcon from '../hooks/openNotificationWithIcon';
import { AUTH_USER_POOL_ID, } from './configs';

class AuthService {
    constructor(clientId, clientSecret, region, identityPoolId) {
        this.clientId = clientId;
        this.clientSecret = clientSecret;
        this.renewToken = this.renewToken.bind(this);

        // Configure AWS region, access key and secret key
        config.update({
            region: region,
            credentials: new CognitoIdentityCredentials({
                IdentityPoolId: identityPoolId,
            }),
        });

        this.cognitoIdentityServiceProvider = new CognitoIdentityServiceProvider();
    }

    calculateSecretHash = (username, clientId, clientSecret) => {
        const message = username + clientId;
        const hash = CryptoJS.HmacSHA256(message, clientSecret);
        return CryptoJS.enc.Base64.stringify(hash);
    };


    async authenticateUser(username, password) {
        try {
            const params = {
                AuthFlow: 'USER_PASSWORD_AUTH',
                ClientId: this.clientId,
                AuthParameters: {
                    USERNAME: username,
                    PASSWORD: password,
                    SECRET_HASH: this.calculateSecretHash(username, this.clientId, this.clientSecret)
                }
            };

            const response = await this.cognitoIdentityServiceProvider.initiateAuth(params).promise();
            if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED' || response?.ChallengeName === "SMS_MFA" || response?.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
                return response;
            }

            localStorage.setItem('unified_nova_user', username);

            return response;
        } catch (err) {
            return err;
        }
    }

    async getUser(access_token) {
        const params = {
            AccessToken: access_token,
        };

        const userData = await this.cognitoIdentityServiceProvider.getUser(params).promise();
        return userData;
    }

    async renewToken(refreshToken) {
        const params = {
            AuthFlow: 'REFRESH_TOKEN_AUTH',
            ClientId: this.clientId,
            AuthParameters: {
                REFRESH_TOKEN: refreshToken,
                SECRET_HASH: this.calculateSecretHash(
                    localStorage.getItem('unified_nova_user'),
                    this.clientId,
                    this.clientSecret
                ),
            },
        };
        const response = await this.cognitoIdentityServiceProvider.initiateAuth(params).promise();
        return {
            AccessToken: response.AuthenticationResult.AccessToken,
            IdToken: response.AuthenticationResult.IdToken,
            RefreshToken: response.AuthenticationResult.RefreshToken || refreshToken,
        };
    }

    async completeNewPassword(username, newPassword, session) {
        try {
            const params = {
                ClientId: this.clientId,
                ChallengeName: 'NEW_PASSWORD_REQUIRED',  // Nome do desafio
                ChallengeResponses: {
                    USERNAME: username,
                    NEW_PASSWORD: newPassword,
                    SECRET_HASH: this.calculateSecretHash(username, this.clientId, this.clientSecret)  // Nova senha que o usuário escolheu
                },
                Session: session,  // Sessão que vem com o resultado do "NEW_PASSWORD_REQUIRED"
            };

            const response = await this.cognitoIdentityServiceProvider.respondToAuthChallenge(params).promise();

            // Se o desafio for completado com sucesso, você pode salvar os tokens.
            // const { IdToken, AccessToken, RefreshToken } = response.AuthenticationResult;
            // const user = {
            //     'cognito:username': username,
            //     'idToken': IdToken,
            //     'accessToken': AccessToken,
            //     'refreshToken': RefreshToken,
            // };
            localStorage.setItem('unified_nova_user', username);

            return response;
        } catch (err) {
            console.error('Erro ao completar a nova senha', err);
            return err;
        }
    };

    // Função para alterar a senha do usuário
    async changePassword(username, newPassword) {
        const params = {
            UserPoolId: AUTH_USER_POOL_ID, // O ID do seu pool de usuários Cognito
            Username: username, // Nome de usuário que deseja alterar a senha
            Password: newPassword, // Nova senha
            Permanent: true, // Se 'true', a nova senha será definitiva. Caso contrário, será necessário alterar a senha em uma próxima autenticação.
        };

        try {
            const result = await this.cognitoIdentityServiceProvider.adminSetUserPassword(params).promise();
            return result;
        } catch (error) {
            console.error('Erro ao alterar a senha:', error);
            throw error; // Lidar com o erro de forma apropriada
        }
    };

    // Solicitar recuperação de senha
    async forgotPassword(username) {
        const params = {
            ClientId: this.clientId, // O ID do seu client
            Username: username, // Nome de usuário para o qual se deseja fazer a recuperação
            SecretHash: this.calculateSecretHash(username, this.clientId, this.clientSecret)
        };

        try {
            const result = await this.cognitoIdentityServiceProvider.forgotPassword(params).promise();
            return result;
        } catch (error) {
            console.error('Erro ao solicitar recuperação de senha:', error);
            throw error; // Lidar com o erro de forma apropriada
        }
    };

    // Confirmar a recuperação de senha
    async confirmForgotPassword(username, confirmationCode, newPassword) {
        const params = {
            ClientId: this.clientId, // O ID do seu client
            Username: username, // Nome de usuário
            ConfirmationCode: confirmationCode, // Código de verificação que o usuário recebeu
            Password: newPassword, // Nova senha que o usuário deseja definir
            SecretHash: this.calculateSecretHash(username, this.clientId, this.clientSecret)  // Nova senha que o usuário escolheu
        };

        try {
            const result = await this.cognitoIdentityServiceProvider.confirmForgotPassword(params).promise();
            return result;
        } catch (error) {
            if (error.code === 'CodeMismatchException') {
                openNotificationWithIcon('error', t('error'), t('Invalid verification code'));
                console.log('Código de verificação inválido');
            } else if (error.code === 'ExpiredCodeException') {
                openNotificationWithIcon('error', t('error'), t('The verification code has expired'));
                console.log('O código de verificação expirou');
            }
            console.error('Erro ao confirmar recuperação de senha:', error);
            throw error; // Lidar com o erro de forma apropriada
        }
    };
}

export default AuthService;